import { AIOptionsFlowType, AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { BEIGE_500, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { Box, Stack, Typography } from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'
import { UploadVisualsTypes, useClientPropertySelfUploadPopup } from './ClientPropertySelfUploadPopup.context'

import { AIEditingProductSettings } from 'components/pages/PurchaseFlow/common/EditingProductListing/AIEditingPhotoSettings'
import AllInclusiveIcon from '@mui/icons-material/AllInclusive'
import { DynamicFlowStep } from 'components/common/DynamicFlow'
import { FC } from 'react'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { MUIBadge } from 'components/common/MUIBadge'
import { MUIButton } from 'components/common/MUIButton'
import { MUICheckCard } from 'components/common/MUICards'
import { UploadVisualsStepKey } from './ClientPropertySelfUploadPopup.controller'
import { externalNavigate } from 'utils/helpers'
import { getPricingUrl } from 'utils/localization'
import i18n from 'translations/i18n'
import { useWorkspacePlan } from 'utils/hooks'

export const UploadOptionsStep: FC = () => {
  const { t } = useTranslation(['self_upload_popup'])

  const { hasWorkspaceActiveSubscription } = useWorkspacePlan()

  const {
    uploadVisualsType,
    setUploadVisualsType,
    isSkyReplacementEnabled,
    selectedSkyReplacement,
    setSelectedSkyReplacement,
    setIsSkyReplacementEnabled
  } = useClientPropertySelfUploadPopup()

  return (
    <DynamicFlowStep
      stepKey={UploadVisualsStepKey.OPTIONS}
      isValid={true}
    >
      <Stack alignItems="center" gap={3} padding="3.2rem 0">

        {Object.values(UploadVisualsTypes).map((key) => {
          if (key === UploadVisualsTypes.AI_EDITING && !hasWorkspaceActiveSubscription) {
            return null
          }

          return (
            <Box width="100%" key={key}>
              <MUICheckCard
                key={key}
                isRadio
                checked={uploadVisualsType === key}
                onCheck={() => {
                  setUploadVisualsType(key)
                  logAnalyticsEvent(AnalyticsEvent.SELF_UPLOAD_OPTIONS_SELECTED, {
                    AIEditingSelected: key === UploadVisualsTypes.AI_EDITING,
                  })
                }}
                expandableContent={key === UploadVisualsTypes.AI_EDITING &&
                  <AIEditingProductSettings
                    isSkyReplacementEnabled={isSkyReplacementEnabled}
                    selectedSkyReplacement={selectedSkyReplacement}
                    setSelectedSkyReplacement={setSelectedSkyReplacement}
                    setIsSkyReplacementEnabled={setIsSkyReplacementEnabled}
                    afterSkyReplacementSet={(params) => {
                      logAnalyticsEvent(AnalyticsEvent.CLICK_AI_EDITING_OPTIONS, {
                        skyReplacement: params?.skyReplacement,
                        skyType: params?.skyType,
                        flow: AIOptionsFlowType.GALLERY
                      })
                    }}
                  />
                }
              >

                <Stack direction="row" gap={1} alignItems="center">

                  <Typography variant="text-md" fontWeight={600} color={GRAY_900}>
                    {t(`upload_visuals_type.${key}.title`)}
                  </Typography>

                  {key === UploadVisualsTypes.AI_EDITING &&
                    <Stack direction="row" gap={1} alignItems="center">

                      <MUIBadge label={t('unlimited_for_now')} size="xs" color="green" icon={<AllInclusiveIcon />} />

                    </Stack>
                  }

                </Stack>

                <Typography variant="text-md" fontWeight={400} color={GRAY_700}>
                  {t(`upload_visuals_type.${key}.description`)}
                </Typography>

              </MUICheckCard>
            </Box>
          )
        })}

        {!hasWorkspaceActiveSubscription &&
          <Stack
            width="100%"
            padding="1.6rem"
            borderRadius="0.8rem"
            justifyContent="flex-start"
            alignItems="flex-start"
            gap="1.2rem"
            sx={{
              backgroundColor: BEIGE_500,
            }}
          >
            <MUIBadge
              color="coral"
              size="sm"
              icon={<LockOutlinedIcon />}
              label={t('subscribers_only_badge')}
            />

            <Stack gap=".4rem">
              <Typography variant="text-md" fontWeight={600} color={GRAY_900}>
                {t('upload_visuals_type.AI_EDITING.title')}
              </Typography>
              <Typography variant="text-sm" fontWeight={400} color={GRAY_700}>
                <Trans
                  t={t}
                  i18nKey="subscribers_only_description"
                >
                  <Typography variant='text-sm' fontWeight={600} color={GRAY_900}></Typography>
                </Trans>
              </Typography>
            </Stack>

            <Stack marginTop=".4rem">
              <MUIButton onClick={() => externalNavigate(getPricingUrl(i18n.language))}>
                {t('subscribe_button')}
              </MUIButton>
            </Stack>
          </Stack>
        }

      </Stack>
    </DynamicFlowStep>
  )
}
